import React from 'react';
import { ArrowRight, CheckCircle2, Target, TrendingUp, Heart } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';
import FAQ from '../components/FAQ';
import { faqMap } from '../data/faqContent';

const PertePoidsVeveyClarens = () => {
  return (
    <>
      <SEO 
        title="Perte de poids à Vevey & Clarens | Méthode durable"
        description="Perte de poids durable à Vevey et Clarens (Montreux). Approche globale combinant nutrition et hypnose. Sans régime restrictif. Cabinet médical."
        keywords="perte de poids Vevey, perte de poids Clarens, maigrir Vevey, programme perte de poids Riviera, nutritionniste perte de poids Montreux"
        url="https://nutricentreriviera.ch/perte-de-poids-vevey-clarens"
      />
      
      <div style={{ marginTop: '100px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'var(--gradient-hero)',
          padding: '4rem 0',
          textAlign: 'center'
        }}>
          <div className="container">
            <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
              Perte de poids à Vevey & Clarens
            </h1>
            <p className="heading-subtitle" style={{ maxWidth: '900px', margin: '0 auto 2rem' }}>
              Retrouver un poids stable sans régime restrictif grâce à une approche globale
            </p>
            <a 
              href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Prendre rendez-vous
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

        {/* Main Content */}
        <section className="section" style={{ background: 'white' }}>
          <div className="container" style={{ maxWidth: '900px' }}>
            {/* Introduction */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem', color: 'var(--accent-primary)' }}>
                Perdre du poids durablement — Méthode complète
              </h2>
              <p className="body-large" style={{ marginBottom: '1rem', lineHeight: '1.8' }}>
                Perdre du poids durablement nécessite une <strong>approche globale</strong>.
              </p>
              <p className="body-large" style={{ marginBottom: '1rem', lineHeight: '1.8' }}>
                Je propose un accompagnement à <strong>Vevey</strong> et <strong>Clarens (Montreux)</strong> pour retrouver 
                un poids stable sans régime restrictif.
              </p>
              <p className="body-large" style={{ lineHeight: '1.8' }}>
                La méthode combine <strong>nutrition</strong>, compréhension du corps et travail sur les comportements 
                alimentaires grâce à l'<strong>hypnose</strong>.
              </p>
            </div>

            {/* Objectifs */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Les objectifs de l'accompagnement
              </h2>
              <div style={{
                background: 'var(--bg-page)',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid var(--border-light)'
              }}>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Perte de poids durable</strong> — Sans effet yoyo ni régime restrictif</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Stabilisation du poids</strong> — Maintenir les résultats sur le long terme</span>
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium"><strong>Amélioration de l'énergie</strong> — Retrouver vitalité et bien-être au quotidien</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* La méthode */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Une méthode complète et durable
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '2rem'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--border-light)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s ease'
                }}
                className="approach-card">
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.75rem',
                    background: 'var(--accent-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem'
                  }}>
                    <Target size={24} style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    Nutrition personnalisée
                  </h3>
                  <p className="body-medium" style={{ lineHeight: '1.6' }}>
                    Plan alimentaire adapté à votre métabolisme et votre mode de vie, sans frustration.
                  </p>
                </div>

                <div style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--border-light)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s ease'
                }}
                className="approach-card">
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.75rem',
                    background: 'var(--accent-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem'
                  }}>
                    <TrendingUp size={24} style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    Hypnose comportementale
                  </h3>
                  <p className="body-medium" style={{ lineHeight: '1.6' }}>
                    Travail sur les compulsions, le stress et les blocages émotionnels liés à l'alimentation.
                  </p>
                </div>
              </div>
            </div>

            {/* Sans régime restrictif */}
            <div style={{ marginBottom: '3rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Sans régime restrictif ni privation
              </h2>
              <div style={{
                background: 'linear-gradient(135deg, var(--accent-primary-light) 0%, rgba(200, 230, 216, 0.3) 100%)',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid var(--accent-primary)',
                borderLeft: '4px solid var(--accent-primary)'
              }}>
                <p className="body-large" style={{ lineHeight: '1.8', marginBottom: '1rem' }}>
                  L'objectif n'est pas de suivre un énième régime, mais de <strong>retrouver une relation saine avec l'alimentation</strong>.
                </p>
                <p className="body-large" style={{ lineHeight: '1.8' }}>
                  L'accompagnement se fait dans la bienveillance, en respectant votre rythme et vos besoins.
                </p>
              </div>

              {/* Localisation */}
              <p className="body-large" style={{ 
                marginTop: '2rem', 
                padding: '1rem',
                background: 'var(--bg-page)',
                borderRadius: '0.75rem',
                fontWeight: 500,
                textAlign: 'center',
                borderLeft: '4px solid var(--accent-primary)'
              }}>
                Cabinet à Vevey et consultations à Clarens (Montreux), facilement accessible depuis toute la Riviera.
              </p>
            </div>

            {/* Consultations */}
            <div style={{ marginBottom: '2rem' }}>
              <p className="body-large" style={{ lineHeight: '1.8', textAlign: 'center' }}>
                Consultations en cabinet à <strong>Vevey</strong>, <strong>Clarens</strong> et <strong>en ligne</strong>.
              </p>
            </div>
          </div>
        </section>

        <FAQ items={faqMap['perte-de-poids-vevey-clarens']} />

        <RelatedPages items={relatedPagesMap['perte-de-poids-vevey-clarens']} />

        {/* CTA Section */}
        <section style={{
          background: 'var(--accent-primary)',
          color: 'white',
          padding: '5rem 0'
        }}>
          <div className="container" style={{ textAlign: 'center' }}>
            <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
              Prêt(e) à commencer votre transformation ?
            </h2>
            <p className="body-large" style={{ 
              color: 'white', 
              opacity: 0.95, 
              maxWidth: '700px', 
              margin: '0 auto 2.5rem' 
            }}>
              Prenez rendez-vous pour une première consultation perte de poids
            </p>
            <a
              href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary" 
              style={{ 
                background: 'white', 
                color: 'var(--accent-primary)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                textDecoration: 'none'
              }}
            >
              Réserver une consultation
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

        <style>{`
          .approach-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 24px rgba(61, 84, 70, 0.15);
            border-color: var(--accent-primary);
          }
        `}</style>
      </div>
    </>
  );
};

export default PertePoidsVeveyClarens;
